import { Routes } from '@/assets/configs'
import { useIsQueryOwner, useIsResponsive, useScrollBlock } from '@/hooks'
import useDisplayDistance from '@/hooks/useDisplayDistance'
import { EmtAvatar, EmtCard, EmtCardProps } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PremiumModal from '@/components/Premium/PremiumModal'
import { useFavoritesV2 } from '@/modules/Favorites'
import NextALink from '@/components/NextALink'
import Box from '@mui/material/Box'
import useCurrentUser from '@/hooks/useCurrentUser'
import { UserCard } from '@/dataV3/Search/UserCard'
import PetsSubView from '@/components/EMT/PetsSubView'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'
import { getProfileActivityBadge } from '@/helpers/profileActivityBadge'

type ProfileCardProps = Omit<
  EmtCardProps,
  'buttonTitle' | 'isNewLabel' | 'premiumLabel' | 'certifiedLabel' | 'onButtonClick' | 'onLikeClick' | 'liked'
> & {
  fullCardLink?: boolean
  profile: UserCard
  blankPage?: boolean
  onButtonClick?: (profile: UserCard) => void | Promise<void>
  contactQuery?: Record<string, string>
  onMobileClickCard?: ReturnType<typeof useFloatingProfile>['handleOpenProfile']
  showPets?: boolean
  messagesVariant?: boolean
}

const ProfileCardV2: React.FC<ProfileCardProps> = ({
  profile,
  fullCardLink = true,
  blankPage = false,
  messagesVariant = false,
  ...props
}) => {
  const { t } = useTranslation('common')
  const [isMobile] = useIsResponsive()
  const router = useRouter()
  const displayDistance = useDisplayDistance()
  const [, , appendQueryType] = useIsQueryOwner(true)
  const [blockScroll, allowScroll] = useScrollBlock()

  const [activePremiumModal, setActivePremiumModal] = useState<boolean>(false)
  const [generateFavorite, isFavoriteLoading] = useFavoritesV2(profile.id, profile['@favorite'])

  const finalDistance = profile?.distance ? displayDistance(Number(profile?.distance)) : '-'
  const profileHref = !profile.animals?.length ? appendQueryType(Routes.Profile(profile.id)) : undefined

  const { isPremium } = useCurrentUser()

  const onButtonClickDefault = useCallback(
    (currentProfile: UserCard) => {
      if (!isPremium) {
        return setActivePremiumModal(true)
      }
      // Get the base parameters for the message route.
      const params = Routes.Message._params.Profile(currentProfile.ownerId || currentProfile.id)

      // Merge in any optional query from props.
      const mergedParams = props.contactQuery ? { ...params, ...props.contactQuery } : params

      // Navigate to the message page with the merged query.
      router.push(appendQueryType(Routes.Message._pathname, mergedParams))
    },
    [isPremium, appendQueryType, props.contactQuery, setActivePremiumModal],
  )

  const {
    distance = finalDistance,
    isNew = profile?.isNew,
    lastRefresh = profile?.lastRefresh,
    premium = profile?.isPremium,
    certified = profile?.isCertified,
    rating = profile?.reviewsSummary?.averageRating,
    onButtonClick = onButtonClickDefault,
    contactQuery,
    onMobileClickCard,
    children,
    showPets = true,
    ...rest
  } = props

  const activityBadge = useMemo(
    () => getProfileActivityBadge({ isNew, lastRefresh, distance: profile?.distance }),
    [isNew, lastRefresh, finalDistance],
  )

  const activityBadgeLabel = useMemo(
    () =>
      activityBadge
        ? t(activityBadge.key, {
            defaultValue: activityBadge.defaultValue,
          })
        : undefined,
    [activityBadge, t],
  )

  const _onButtonClickInternal = useCallback(() => {
    if (onButtonClick) {
      onButtonClick(profile as any)
    }
  }, [profile, onButtonClick])

  const titleNode = useMemo(() => {
    const node = <span className="titleNode">{profile.displayName}</span>

    if (!isMobile && profileHref) {
      return (
        <NextALink href={profileHref} forceExternal={!isMobile && blankPage}>
          {node}
        </NextALink>
      )
    }

    return node
  }, [profile.displayName, isMobile, blankPage, profileHref])

  const img = useMemo(() => {
    const node = (
      <EmtAvatar className={'profile-card-main-avatar'} size="large" variant="rounded" src={profile.thumbnail} />
    )

    if (!isMobile && profileHref) {
      return (
        <NextALink href={profileHref} forceExternal={!isMobile && blankPage}>
          {node}
        </NextALink>
      )
    }
    return node
  }, [profile.thumbnail, isMobile, blankPage, profileHref])

  useEffect(() => {
    return () => allowScroll()
  }, [])

  useEffect(() => {
    if (activePremiumModal) blockScroll()
    if (!activePremiumModal) allowScroll()
  }, [activePremiumModal])

  const _onLikeClickInternal = useCallback(
    async (liked: boolean) => {
      const result = await generateFavorite(profile.isFavorite)
      if (result) {
        profile.isFavorite = !profile.isFavorite
      }
    },
    [generateFavorite, profile.isFavorite],
  )

  // NEW: When clicking anywhere on the card (except on interactive elements)
  // navigate to the profile page.
  const handleMobileCardClick = useCallback(() => {
    if (!profile.animals?.length) {
      if (onMobileClickCard) {
        onMobileClickCard(profile.id, ProfileKind.Community)
      } else if (profileHref) {
        router.push(profileHref)
      }
    }
  }, [onMobileClickCard, profileHref, profile.animals, props.variant, profile.id])

  const animalsChildren = useMemo(() => {
    if (profile.animals?.length && showPets) {
      return <PetsSubView pets={profile.animals} onMobileClickCard={onMobileClickCard} sx={{ mb: 2 }} />
    }
    return null
  }, [profile.animals, showPets])

  const card = (
    <EmtCard
      buttonTitle={t('profileCard.contact')!}
      distance={distance}
      liked={profile.isFavorite}
      likeLoader={isFavoriteLoading}
      isNew={!!activityBadgeLabel}
      isNewLabel={activityBadgeLabel}
      premium={!messagesVariant ? profile.isPremium : false}
      premiumLabel={t('profileCard.premium')!}
      certified={!messagesVariant ? profile.isCertified : false}
      certifiedLabel={t('profileCard.idChecked')!}
      showRating={!!rating}
      rating={typeof rating === 'number' ? Math.round(rating * 100) / 100 : rating}
      titleNode={titleNode}
      img={showPets ? img : null}
      onButtonClick={_onButtonClickInternal}
      onLikeClick={_onLikeClickInternal}
      {...rest}
    >
      {children}
      {animalsChildren}
    </EmtCard>
  )

  return (
    <>
      {/* Wrap the card in a clickable container */}
      {isMobile ? (
        // Mobile: use onClick handler on the card container
        fullCardLink && !profile?.animals?.length ? (
          <Box
            onClick={handleMobileCardClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleMobileCardClick()
              }
            }}
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              '&:focus-visible': {
                outline: '2px solid #1D1929',
                outlineOffset: '2px',
                borderRadius: '18px',
              },
            }}
            aria-label={`Voir le profil de ${profile.displayName}`}
            role="link"
            tabIndex={0}
          >
            {card}
          </Box>
        ) : (
          card
        )
      ) : (
        // Desktop: will use NextALink for navigation on title and image
        <Box sx={{ '& a': { textDecoration: 'none!important' } }}>{card}</Box>
      )}
      {/* No premium modal */}
      <PremiumModal
        isFixed
        hideNavigation
        activeSecondaryContent
        open={activePremiumModal}
        onClose={() => setActivePremiumModal(false)}
        zIndex={isMobile ? 999999 : 998}
      />
      {/* END No premium modal */}
    </>
  )
}

export default ProfileCardV2
